import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const Work: React.FC = () => {
  const portfolioProjects = [
    {
      id: 1,
      title: 'ESP Aust',
      industry: 'Business Services',
      description: 'Professional business services website with modern design and user-friendly interface.',
      url: 'https://espaust.net.au/',
      technologies: ['React', 'Tailwind CSS', 'Responsive Design'],
      image: '🏢'
    },
    {
      id: 2,
      title: 'Test Mate',
      industry: 'Testing Services',
      description: 'Comprehensive testing services platform with automated booking and scheduling.',
      url: 'https://testmate.com.au/',
      technologies: ['React', 'Node.js', 'MongoDB'],
      image: '✅'
    },
    {
      id: 3,
      title: 'Clarke Global',
      industry: 'Global Consulting',
      description: 'Corporate consulting website with international presence and service portfolio.',
      url: 'http://www.clarkeglobal.com.au/',
      technologies: ['React', 'TypeScript', 'API Integration'],
      image: '🌍'
    },
    {
      id: 4,
      title: 'Cherry Lash',
      industry: 'Beauty & Wellness',
      description: 'Modern e-commerce platform for beauty products with online booking system.',
      url: 'https://cherrylash.com/',
      technologies: ['React', 'E-commerce', 'Payment Integration'],
      image: '💄'
    },
    {
      id: 5,
      title: 'Skyline Consultancy',
      industry: 'Professional Services',
      description: 'Consultancy services website with client portal and project management features.',
      url: 'https://skylineconsultancy.co.uk/',
      technologies: ['React', 'Node.js', 'PostgreSQL'],
      image: '🏗️'
    },
    {
      id: 6,
      title: 'Reptile Encounters',
      industry: 'Education & Entertainment',
      description: 'Interactive website for educational reptile shows with booking and event management.',
      url: 'https://www.reptileencounters.com.au/',
      technologies: ['React', 'Booking System', 'CMS'],
      image: '🦎'
    },
    {
      id: 7,
      title: 'Neptune Realty Group',
      industry: 'Real Estate',
      description: 'Real estate platform with property listings, search functionality, and lead management.',
      url: 'https://neptunerealtygroup.com',
      technologies: ['React', 'Property API', 'CRM Integration'],
      image: '🏠'
    },
    {
      id: 8,
      title: 'Hope Care 4 All',
      industry: 'Healthcare & Support Services',
      description: 'Comprehensive healthcare and support services platform providing care solutions for individuals and families.',
      url: 'https://www.hopecare4all.com.au/',
      technologies: ['React', 'Healthcare CMS', 'Service Management'],
      image: '💙'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 text-white">
        <div className="container-custom section-padding">
          <div className="text-center max-w-4xl mx-auto">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Our Work
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl text-primary-100 mb-8 leading-relaxed"
            >
              Discover how we've helped organizations across industries transform their IT infrastructure, 
              enhance security, and drive business success through innovative technology solutions.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Portfolio Projects */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Our Portfolio
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Explore our diverse range of successful projects across multiple industries. 
              Click on any project to visit the live website.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioProjects.map((project, index) => (
              <motion.a
                key={project.id}
                href={project.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group border border-secondary-200 hover:border-primary-400"
              >
                <div className="bg-gradient-to-br from-primary-500 to-accent-600 p-8 text-center">
                  <div className="text-6xl mb-4">{project.image}</div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {project.title}
                  </h3>
                  <span className="inline-block bg-white/20 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {project.industry}
                  </span>
                </div>
                <div className="p-6">
                  <p className="text-secondary-600 mb-4 min-h-[60px]">
                    {project.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech, idx) => (
                      <span key={idx} className="bg-secondary-100 text-secondary-700 px-3 py-1 rounded-full text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center text-primary-600 group-hover:text-primary-700 font-medium">
                    <span>Visit Website</span>
                    <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-padding bg-secondary-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">
              Proven Track Record
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Numbers that speak for themselves - delivering excellence across every project.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center bg-white p-6 rounded-xl shadow-md"
            >
              <div className="text-4xl font-bold text-primary-600 mb-2">100+</div>
              <div className="text-secondary-900 font-semibold">Projects Delivered</div>
              <div className="text-secondary-600 text-sm">Across all industries</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-center bg-white p-6 rounded-xl shadow-md"
            >
              <div className="text-4xl font-bold text-primary-600 mb-2">99%</div>
              <div className="text-secondary-900 font-semibold">Client Satisfaction</div>
              <div className="text-secondary-600 text-sm">Based on feedback</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-center bg-white p-6 rounded-xl shadow-md"
            >
              <div className="text-4xl font-bold text-primary-600 mb-2">20+</div>
              <div className="text-secondary-900 font-semibold">Years Experience</div>
              <div className="text-secondary-600 text-sm">In software development</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="text-center bg-white p-6 rounded-xl shadow-md"
            >
              <div className="text-4xl font-bold text-primary-600 mb-2">24/7</div>
              <div className="text-secondary-900 font-semibold">Support Available</div>
              <div className="text-secondary-600 text-sm">For all clients</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you achieve similar results for your organization.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700 text-white">
              Start Your Project
            </Link>
            <Link to="/services" className="btn-secondary bg-transparent border-2 border-white text-white hover:bg-cyan-500 hover:text-white">
              Explore Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Work;
